import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './style.module.css'
import { Button } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { Route } from '../../config/router'
import { Terms } from './Terms'
import { PayInFourCta } from '../PayInFourCta'
import { getPaymentOptions, useAppDispatch, useAppRouter, useCheckoutToken, useCountry, useCreateFacilityMutation } from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { Country } from '@afterpay/types'
import { useHandlePaymentOptionsError } from '../PaymentOptions/hooks'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model'

interface CreditFooterProps {
  contractId?: string
}

export const ApprovedCreditFooter: FunctionComponent<CreditFooterProps> = ({ contractId }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [lock, setLock] = useState<boolean>(false)
  const token = useCheckoutToken()
  const [createFacility, { isLoading: isCreateFacilityLoading, isSuccess: isCreateFacilitySuccess, reset }] = useCreateFacilityMutation({
    fixedCacheKey: 'create-facility-cache',
  })
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { handlePaymentOptionsError } = useHandlePaymentOptionsError()
  const [paymentOptionsFetching, setPaymentOptionsFetching] = useState(false)
  const dispatch = useAppDispatch()
  const country = useCountry()
  const isPCLAffordabilityEnabled = useFlag(Features.CONSUMER_LENDING_AFFORDABILITY_ENABLED)
  const isConsumerLendingFacilityTesting = useFlag(Features.CONSUMER_LENDING_FACILITY_TESTING)

  useEffect(() => {
    router.prefetch(Route.PAYMENT_OPTIONS)
    reset()
  }, [])

  const handleAgreeClick = async (): Promise<void> => {
    setLock(true)
    logEvent(TrackingEvent.PRESSED_CL_APPROVED_CREDIT_PAGE_CONTINUE_BUTTON)
    createFacility({ token })
  }

  useEffect(() => {
    const doGetPaymentOptions = async (): Promise<void> => {
      try {
        setPaymentOptionsFetching(true)
        await dispatch(getPaymentOptions()).unwrap()
        logEvent(TrackingEvent.VIEWED_CL_PAYMENT_OPTIONS_PAGE)
      } catch (e) {
        handlePaymentOptionsError(e)
      }
      router.push(Route.PAYMENT_OPTIONS)
    }

    if (isCreateFacilitySuccess) {
      if (country === Country.AU && isPCLAffordabilityEnabled) {
        doGetPaymentOptions()
      } else {
        router.push(Route.PAYMENT_OPTIONS)
      }
    }
  }, [isCreateFacilitySuccess])

  return (
    <div className={styles.approvedCreditFooter}>
      <Terms documentId={contractId} />
      <div className={styles.approvedCreditAgreeButton}>
        <Button.Primary
          loading={paymentOptionsFetching || isCreateFacilityLoading}
          disabled={lock || isConsumerLendingFacilityTesting}
          type='button'
          padding='Fluid'
          onClick={handleAgreeClick}
          testNameSpace='agree'
        >
          {t('consumerLending:approvedCredit:agree')}
        </Button.Primary>
      </div>
      <PayInFourCta trackingEvent={TrackingEvent.PRESSED_CL_APPROVED_CREDIT_PAGE_PAY_IN_4_BUTTON} />
    </div>
  )
}
